/**
 * The full list of capabilities GamePlan/BuilderPlan can gate, and #510's
 * display name for each — the single TypeScript definition of what
 * capabilities exist (issue #567, design decisions 1, 2 and 13).
 *
 * `#gp/app-common/util/capabilities.ts` derives its `CapabilityKey` type and
 * `ALL_CAPABILITY_KEYS` export from `CANONICAL_CAPABILITY_KEYS` below, so the
 * key list itself exists exactly once in TypeScript. AdminPortalWeb's
 * licence-type capability grid
 * (`AdminPortalWeb/src/features/licenses/LicenseTypeCapabilitiesCard.tsx`)
 * imports THIS FILE directly, through `#gp/app-common/*` — the SAME alias
 * family GamePlanNodeJsFunctions already uses to share large amounts of
 * GamePlanWebRole's TypeScript (see GamePlanWebRole/AGENTS.md, "Module
 * imports — use the #gp/* path aliases"). AdminPortalWeb declares only the
 * one entry it needs (`#gp/app-common/*`) in its own
 * `tsconfig.app.json` / `tsconfig.vitest.json` and
 * `vite.config.ts` / `vitest.config.ts`.
 *
 * THIS FILE IS NONETHELESS DEPENDENCY-FREE — no imports, not even the other
 * `#gp/*` aliases the rest of `app-common` uses. That is a SIMPLICITY
 * CHOICE, not a hard requirement — AdminPortalWeb's `#gp/app-common/*` alias
 * would resolve an import here same as GamePlanWebRole's own code does. But
 * this module has exactly one job, a name lookup, and an import would drag
 * in whatever that dependency itself depends on (e.g. `subscription.models`
 * and its own chain) for no benefit — bloating the AdminPortal bundle to
 * resolve a display name. Keep it import-free unless a real need appears.
 *
 * Display names live here, in TypeScript, and are NOT added to
 * GamePlan.FeatureFlagging.CapabilityDefinition (C#): the product UI decides
 * when and how to show a feature and owns the canonical list of what
 * exists, and the capability API payload
 * (`GET/PUT /api/license-types/{key}/capabilities`) carries key/value pairs
 * only, never a name.
 *
 * Every #510 matrix row is present, including the rows that are currently
 * Yes/Yes/Yes and the AI row: a capability being universally enabled today
 * does not mean it always will be, and listing it here is not the same as
 * gating it.
 *
 * The list is NOT closed at #510's rows. `baselines` and `calendarView`
 * (#676) shipped after the matrix was drawn and are gateable in exactly the
 * same way; #510's matrix simply has no row for them yet, which is a decision
 * about which tier carries them, not about whether they belong here.
 */
export const CANONICAL_CAPABILITY_KEYS = [
    'aiFeatures',
    'pdfExport',
    'pngExport',
    'scheduleAnimation',
    'executiveAndRiskReports',
    'excelExport',
    'receiveSharedPlan',
    'progressAndTimesheets',
    'spreadsheetImport',
    'subtasks',
    'meetings',
    'equipmentResources',
    'templates',
    'lockedTasks',
    'dynamicReports',
    'shareByEmail',
    'msProjectImport',
    'msProjectExport',
    'primaveraImport',
    'primaveraExport',
    'costing',
    'baselines',
    'calendarView',
] as const;

export type CanonicalCapabilityKey = typeof CANONICAL_CAPABILITY_KEYS[number];

/**
 * #510's display name for each capability, exactly as the matrix words it.
 * Typed as a `Record` keyed by `CanonicalCapabilityKey` rather than a lookup
 * map, so leaving a canonical key without a display name is a COMPILE
 * error, not something only a test catches.
 */
export const CAPABILITY_DISPLAY_NAMES: Readonly<Record<CanonicalCapabilityKey, string>> = {
    aiFeatures: 'All AI features (Planner, chat, expand, analysis, Coach)',
    pdfExport: 'PDF export (Gantt)',
    pngExport: 'PNG export (whiteboard)',
    scheduleAnimation: 'Schedule animation',
    executiveAndRiskReports: 'Executive summary and risk reports',
    excelExport: 'Excel export',
    receiveSharedPlan: 'Receive a shared plan and edit it',
    progressAndTimesheets: 'Set task % complete, enter timesheets',
    spreadsheetImport: 'Spreadsheet import (Excel, Google Sheets)',
    subtasks: 'Subtasks',
    meetings: 'Meetings',
    equipmentResources: 'Equipment resources',
    templates: 'Templates',
    lockedTasks: 'Locked tasks',
    dynamicReports: 'Dynamic reports',
    shareByEmail: 'Share plan by email (inviting a collaborator)',
    msProjectImport: 'MS Project import',
    msProjectExport: 'MS Project export',
    primaveraImport: 'P6 import',
    primaveraExport: 'P6 export',
    costing: 'Costing',
    baselines: 'Baselines',
    calendarView: 'Calendar view',
};

/**
 * Is `key` one of the canonical capability keys above? Narrows a plain
 * `string` (what an API response actually hands back) to
 * `CanonicalCapabilityKey`, which is what makes it safe to index
 * `CAPABILITY_DISPLAY_NAMES` with a value that did not originate as a
 * `CanonicalCapabilityKey` literal.
 */
export function isCanonicalCapabilityKey(key: string): key is CanonicalCapabilityKey {
    return Object.prototype.hasOwnProperty.call(CAPABILITY_DISPLAY_NAMES, key);
}

/**
 * The display name for a capability key, or the raw key itself when the key
 * is not canonical.
 *
 * The fallback is deliberate, not a placeholder: it is how a retired
 * capability key that still holds a database row becomes visible in the
 * AdminPortal grid — and therefore deletable. Without it, such a row is
 * invisible, which makes config health's "delete the row(s) for the
 * retired key" remediation impossible to follow.
 */
export function displayNameForCapabilityKey(key: string): string {
    return isCanonicalCapabilityKey(key) ? CAPABILITY_DISPLAY_NAMES[key] : key;
}
